"use client";

import { useId, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FormOption } from "./searchable-combobox";
import { z } from "zod";

interface MultiSelectComboboxFieldProps {
  name: string;
  label: string;
  options: FormOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  required?: boolean;
  disabled?: boolean;
  maxVisibleChips?: number;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onValueChange?: (values: string[]) => void;
  useSavedLabels?: boolean;
}

const multiSelectValueSchema = z.union([
  z.string(),
  z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
]);
type MultiSelectValue = z.infer<typeof multiSelectValueSchema>;

function normalizeValues(value: MultiSelectValue): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const normalized = String(item).trim();
      return normalized ? [normalized] : [];
    });
  }

  if (!value.trim()) {
    return [];
  }

  return value
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function MultiSelectComboboxField({
  name,
  label,
  options,
  placeholder = "Selecciona una o más opciones",
  searchPlaceholder = "Buscar...",
  emptyMessage = "No hay opciones disponibles",
  required = false,
  disabled = false,
  maxVisibleChips = 3,
  loading = false,
  error = null,
  onRetry,
  onValueChange,
  useSavedLabels = false,
}: MultiSelectComboboxFieldProps) {
  const { control, setValue } = useFormContext();
  const labels: unknown = useWatch({ control, name: `${name}_label` });
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const parsedValue = multiSelectValueSchema.safeParse(field.value);
        const selectedValues = parsedValue.success ? normalizeValues(parsedValue.data) : [];
        const selectedValueSet = new Set(selectedValues);
        const visibleChipValues = selectedValues.slice(0, maxVisibleChips);

        const parsedLabels = z.string().safeParse(labels);
        const savedLabels = parsedLabels.success ? parsedLabels.data.split(";").map(label => label.trim()) : [];
        const toLabel = (value: string) => {
          const optionLabel = options.find((option) => option.value === value)?.label;
          return useSavedLabels ? optionLabel?.trim() || savedLabels[selectedValues.indexOf(value)] || value : optionLabel ?? value;
        };

        const applySelection = (nextValues: string[]) => {
          if (disabled || loading || error) return;
          field.onChange(nextValues.join(";"));
          setValue(`${name}_label`, nextValues.map(toLabel).join("; "), {
            shouldValidate: true,
          });
          onValueChange?.(nextValues);
        };

        const toggleValue = (value: string) => {
          const isSelected = selectedValueSet.has(value);
          const nextValues = isSelected
            ? selectedValues.filter((selectedValue) => selectedValue !== value)
            : [...selectedValues, value];
          applySelection(nextValues);
        };

        return (
          <FormItem className="flex flex-col">
            <FormLabel>
              {label} {required && "*"}
            </FormLabel>
            <Popover
              open={open}
              onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                  setSearch("");
                }
              }}
            >
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={listId}
                    disabled={disabled}
                    className={cn(
                      "h-auto min-h-9 w-full justify-between py-1.5",
                      selectedValues.length === 0 && "text-muted-foreground",
                      "disabled:bg-muted"
                    )}
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-1 text-left">
                      {selectedValues.length === 0 ? (
                        <span>{placeholder}</span>
                      ) : (
                        <>
                          {visibleChipValues.map((value) => (
                            <Badge
                              key={value}
                              variant="secondary"
                              className="max-w-full truncate font-normal"
                            >
                              {toLabel(value)}
                            </Badge>
                          ))}
                          {selectedValues.length > maxVisibleChips ? (
                            <Badge variant="outline" className="font-normal">
                              +{selectedValues.length - maxVisibleChips}
                            </Badge>
                          ) : null}
                        </>
                      )}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
              >
                <Command>
                  <CommandInput
                    placeholder={searchPlaceholder}
                    value={search}
                    onValueChange={setSearch}
                    aria-label={searchPlaceholder}
                  />
                   <CommandList id={listId}>
                    {error ? <div className="px-3 py-4 text-sm text-destructive" role="alert">{error}</div> : loading ? <div className="flex items-center justify-center gap-2 py-6 text-sm"><Spinner /> Cargando…</div> : <>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        const isSelected = selectedValueSet.has(option.value);
                        return (
                          <CommandItem
                            key={option.value}
                            value={`${option.label} ${option.value}`}
                            onSelect={() => toggleValue(option.value)}
                          >
                            <span
                              className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-[4px] border",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-input"
                              )}
                            >
                              <Check
                                className={cn(
                                  "h-3 w-3",
                                  isSelected ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </span>
                            {option.displayLabel || option.label}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                    </>}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
            {error && <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
              <span>{error}</span>
              {onRetry && <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onRetry}>Reintentar</Button>}
            </div>}
          </FormItem>
        );
      }}
    />
  );
}
